/* eslint-disable no-restricted-syntax */
// The DDPP injunction (ML-25365) imposes that this exact wording — including
// the legal names FONCIA GRESIVAUDAN / FONCIA ALPES DAUPHINE / FONCIA AGDA — be
// displayed verbatim on the customer portal. The white-label
// `no-restricted-syntax` rule is disabled for this file only because we are
// reproducing a regulatory notice, not branding text.
import { styled } from "@mui/material";
import React from "react";

const DDPP_URL = "https://www.economie.gouv.fr/dgccrf/injonctions-et-sanctions";
const MORE_INFO_URL =
  "https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/le-contrat-un-accord-entre-des-parties-bien-informees-au-prealable";

export type DdppBannerVariant = "GRESIVAUDAN" | "ALPES_DAUPHINE" | "AGDA";

const StyledRoot = styled("a")(({ theme }) => ({
  display: "block",
  width: "100%",
  backgroundColor: theme.palette.common.black,
  color: theme.palette.common.white,
  fontFamily: "Arial, sans-serif",
  textDecoration: "none",
  padding: theme.spacing(4),
  cursor: "pointer",

  "&:hover, &:focus-visible": {
    textDecoration: "underline",
    outline: "none",
  },
}));

const StyledTitle = styled("p")(({ theme }) => ({
  fontWeight: "bold",
  textAlign: "center",
  fontSize: theme.typography.pxToRem(18),
  margin: 0,
  marginBottom: theme.spacing(3),
}));

const StyledParagraph = styled("p")(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  lineHeight: 1.5,
  margin: 0,
  marginBottom: theme.spacing(2),

  "&:last-child": {
    marginBottom: 0,
  },
}));

const StyledStrong = styled("span")({
  display: "block",
  fontWeight: "bold",
});

const StyledMoreInfo = styled("span")(({ theme }) => ({
  display: "block",
  fontStyle: "italic",
  fontSize: theme.typography.pxToRem(13),
  marginTop: theme.spacing(2),
}));

type BannerContent = {
  ariaLabel: string;
  title: string;
  body: React.ReactNode;
};

const GRESIVAUDAN_CONTENT: BannerContent = {
  ariaLabel:
    "Information DDPP - Injonction faite à FONCIA GRESIVAUDAN de cesser les pratiques commerciales trompeuses",
  title:
    "La SAS FONCIA GRESIVAUDAN enjointe de cesser ses pratiques commerciales trompeuses et de respecter la réglementation en matière d'immobilier",
  body: (
    <>
      <StyledParagraph>
        Suite à la réception de réclamations déposées via la plateforme gouvernementale SignalConso,
        un contrôle a été effectué par les agents de la concurrence, de la consommation et de la
        répression des fraudes (CCRF) affectés à la direction départementale de la protection des
        populations (DDPP) de l&#39;Isère, concernant la société par action simplifiée FONCIA
        GRESIVAUDAN (siret n° 90044068600088) ayant pour activité la gestion locative.
      </StyledParagraph>

      <StyledParagraph>
        <StyledStrong>
          L&#39;enquête de la DDPP de l&#39;Isère a révélé que la SAS FONCIA GRESIVAUDAN a facturé à
          plusieurs reprises des frais de résiliation de manière indue aux consommateurs qui
          souhaitaient mettre fin à leur mandat de gestion locative, entre le 1er janvier 2023 et le
          31 mars 2024.
        </StyledStrong>
      </StyledParagraph>

      <StyledParagraph>
        <StyledStrong>
          La SAS FONCIA GRESIVAUDAN avait également facturé de manière injustifiée des frais de mise
          en location sans qu&#39;ils ne soient prévus aux mandats de gestion locative entre le 1er
          juin 2024 et le 31 mars 2025.
        </StyledStrong>
      </StyledParagraph>

      <StyledParagraph>
        <StyledStrong>Ces pratiques sont interdites par le Code de la consommation.</StyledStrong>
      </StyledParagraph>

      <StyledParagraph>
        La DDPP de l&#39;Isère a donc enjoint à la SAS FONCIA GRESIVAUDAN de cesser ces pratiques
        sous 30 jours, sous peine de sanction.
      </StyledParagraph>
    </>
  ),
};

const ALPES_DAUPHINE_CONTENT: BannerContent = {
  ariaLabel:
    "Information DDPP - Injonction faite à FONCIA ALPES DAUPHINE de cesser les pratiques commerciales trompeuses",
  title:
    "La SAS FONCIA ALPES DAUPHINE enjointe de cesser ses pratiques commerciales trompeuses et de respecter la réglementation en matière d'immobilier",
  body: (
    <>
      <StyledParagraph>
        Suite à la réception de réclamations déposées via la plateforme gouvernementale SignalConso,
        une enquête a été effectuée par les agents de la concurrence, de la consommation et de la
        répression des fraudes (CCRF) affectés à la direction départementale de la protection des
        populations (DDPP) de l&#39;Isère, concernant la société par action simplifiée FONCIA ALPES
        DAUPHINE (siret n° 30553251700037), ayant notamment pour activité la gestion locative.
      </StyledParagraph>

      <StyledParagraph>
        <StyledStrong>
          L&#39;enquête de la DDPP de l&#39;Isère a révélé que la SAS FONCIA ALPES DAUPHINE a
          facturé à plusieurs reprises des frais de résiliation de manière indue aux consommateurs
          qui souhaitaient mettre fin à leur mandat de gestion locative, entre le 1er octobre 2021
          et le 31 décembre 2021, le 1er avril 2024 et le 31 décembre 2024.
        </StyledStrong>
      </StyledParagraph>

      <StyledParagraph>
        <StyledStrong>
          La SAS FONCIA ALPES DAUPHINE avait également facturé de manière injustifiée des frais de
          mise en location sans qu&#39;ils ne soient prévus aux mandats de gestion locative entre le
          1er octobre 2024 et le 31 mars 2025.
        </StyledStrong>
      </StyledParagraph>

      <StyledParagraph>
        <StyledStrong>
          Enfin, la SAS FONCIA ALPES DAUPHINE n&#39;inscrivait pas de manière systématique le
          montant des dépenses énergétiques théoriques correspondant au diagnostic de performance
          énergétique, et se référait à un diagnostic de performance énergétique obsolète au sein
          des annonces de location.
        </StyledStrong>
      </StyledParagraph>

      <StyledParagraph>
        <StyledStrong>
          Ces pratiques sont interdites par le Code de la consommation et le Code de la construction
          et de l&#39;habitation.
        </StyledStrong>
      </StyledParagraph>

      <StyledParagraph>
        La DDPP de l&#39;Isère a donc enjoint à la SAS FONCIA ALPES DAUPHINE de cesser ces pratiques
        sous 30 jours, sous peine de sanction.
      </StyledParagraph>
    </>
  ),
};

const AGDA_CONTENT: BannerContent = {
  ariaLabel:
    "Information DDPP - Injonction faite à FONCIA AGDA de cesser les pratiques commerciales trompeuses",
  title:
    "La SAS FONCIA AGDA enjointe de cesser ses pratiques commerciales trompeuses et de respecter la réglementation en matière d'immobilier",
  body: (
    <>
      <StyledParagraph>
        Un contrôle a été effectué par les agents de la concurrence, de la consommation et de la
        répression des fraudes (CCRF) affectés à la direction départementale de la protection des
        populations (DDPP) de l&#39;Isère, auprès de la société par action simplifiée FONCIA AGDA
        (siret n° 39336986300013) ayant pour activité la gestion locative.
      </StyledParagraph>

      <StyledParagraph>
        <StyledStrong>
          L&#39;enquête de la DDPP de l&#39;Isère a révélé que la SAS FONCIA AGDA a facturé de
          manière injustifiée des frais de mise en location sans qu&#39;ils ne soient prévus aux
          mandats de gestion locative entre le 1er juin 2024 et le 31 janvier 2025.
        </StyledStrong>
      </StyledParagraph>

      <StyledParagraph>
        <StyledStrong>Cette pratique est interdite par le Code de la consommation.</StyledStrong>
      </StyledParagraph>

      <StyledParagraph>
        La DDPP de l&#39;Isère a donc enjoint à la SAS FONCIA AGDA de cesser ces pratiques sous 30
        jours, sous peine de sanction.
      </StyledParagraph>
    </>
  ),
};

const CONTENT_BY_VARIANT: Record<DdppBannerVariant, BannerContent> = {
  GRESIVAUDAN: GRESIVAUDAN_CONTENT,
  ALPES_DAUPHINE: ALPES_DAUPHINE_CONTENT,
  AGDA: AGDA_CONTENT,
};

type Props = {
  variant: DdppBannerVariant;
};

export const DdppLegalBanner = ({ variant }: Props) => {
  const { ariaLabel, title, body } = CONTENT_BY_VARIANT[variant];

  return (
    <StyledRoot href={DDPP_URL} target="_blank" rel="noopener noreferrer" aria-label={ariaLabel}>
      <StyledTitle>{title}</StyledTitle>
      {body}
      <StyledParagraph>
        <StyledMoreInfo>
          Pour plus d&#39;informations sur vos droits en matière de gestion locative et de mandats,
          consultez le lien suivant :{" "}
          <a
            href={MORE_INFO_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "underline" }}
            onClick={(event) => event.stopPropagation()}
          >
            {MORE_INFO_URL}
          </a>
        </StyledMoreInfo>
      </StyledParagraph>
    </StyledRoot>
  );
};
